/* eslint-disable jsx-a11y/label-has-associated-control */
/* eslint-disable react-hooks/exhaustive-deps */
import { debounce, isNil } from 'lodash';
import {
  memo,
  useCallback,
  useEffect,
  useRef,
  useState,
  type RefObject,
  type FocusEvent,
  type KeyboardEvent,
} from 'react';
import { useIntl } from 'react-intl';
import NumberFormat, { type NumberFormatValues } from 'react-number-format';
import { areEqualFields, onKeyDown } from 'utils/form';
import { isFiniteNumber, type INumberFormatSeparators } from '../numbers';
import type { TFieldComponentProps } from '../types';
import valueProp from '../valueProp';
import s from './SliderWithTooltip.module.scss';

export interface ISliderInputProps {
  form: TFieldComponentProps<unknown>['form'];
  name: string;
  min: number;
  max: number;
  units: string;
  value: number | null | undefined;
  setValue: (value: number | null | undefined) => void;
  sliderValue: number | null | undefined;
  forciblyReplaceInputValue?: boolean;
  thousandSeparator?: string | boolean;
  sideEffectOnChange?: (value: number | null) => void;
  changeableStep: boolean;
  setChangeableStep: (changeableStep: boolean) => void;
  fieldWrapperRef: RefObject<HTMLDivElement>;
}

const config: Record<string, INumberFormatSeparators> = {
  en: {
    thousandSeparator: ',',
    decimalSeparator: '.',
  },
  de: {
    thousandSeparator: '.',
    decimalSeparator: ',',
  },
};

const Input = ({
  form,
  name,
  min,
  max,
  units,
  value,
  setValue,
  sliderValue,
  forciblyReplaceInputValue = false,
  thousandSeparator,
  sideEffectOnChange,
  changeableStep,
  setChangeableStep,
  fieldWrapperRef,
}: ISliderInputProps) => {
  const { locale } = useIntl();
  const inputRef = useRef<NumberFormat>(null);
  const [, forceRerender] = useState(false);
  const [shouldInputValueBeEqualsSlider, setInputValueBeEqualsSlider] = useState(false);

  useEffect(() => {
    if (shouldInputValueBeEqualsSlider && sliderValue !== value) {
      setValue(sliderValue);
    }
  }, [setValue, shouldInputValueBeEqualsSlider, sliderValue, value]);

  const handleChange = useCallback(
    debounce((_values: NumberFormatValues) => {
      setInputValueBeEqualsSlider(false);
      let v;
      if (isNil(_values.floatValue)) {
        v = null;
      } else {
        v = _values.floatValue > max ? max : _values.floatValue;
      }
      setValue(v);

      if (typeof sideEffectOnChange === 'function') sideEffectOnChange(v);
      if (isFiniteNumber(v) && v >= min && v <= max) form.setFieldValue(name, v);

      forceRerender((b) => !b);
    }, 500),
    [max, setValue],
  );

  const handleFocus = useCallback(
    (e: FocusEvent<HTMLInputElement>) => {
      e.target.focus();
      if (changeableStep) {
        setChangeableStep(false);
      }
      if (forciblyReplaceInputValue) {
        setInputValueBeEqualsSlider(true);
      }
    },
    [changeableStep, setChangeableStep],
  );

  const handleBlur = useCallback(() => {
    // A click on the submit button blurs the input first: the typed value must reach the form
    // before the submit reads it, not 500 ms later.
    handleChange.flush();
    form.setFieldTouched(name, true);
    if (forciblyReplaceInputValue) {
      setInputValueBeEqualsSlider(true);
    }
  }, [forciblyReplaceInputValue, form, handleChange, name]);

  const handleKeyDown = useCallback(
    (e: KeyboardEvent<HTMLInputElement>) => {
      onKeyDown(e, fieldWrapperRef);
    },
    [fieldWrapperRef],
  );

  return (
    <div className={s.inputWrapper}>
      <label htmlFor={`${name}-number-input`}>
        <NumberFormat
          ref={inputRef}
          max={max}
          {...valueProp(value)}
          allowNegative={false}
          allowLeadingZeros={false}
          allowEmptyFormatting={false}
          {...(!isNil(thousandSeparator) ? { thousandSeparator } : config[locale])}
          name={`${name}-number-input`}
          autoComplete="off"
          id={`${name}-number-input`}
          onFocus={handleFocus}
          onKeyDown={handleKeyDown}
          className={s.numberInput}
          type="text"
          onValueChange={handleChange}
          onBlur={handleBlur}
        />
      </label>
      <div className={s.units}>{units}</div>
    </div>
  );
};

export default memo(Input, (prevProps, nextProps) =>
  areEqualFields(prevProps, nextProps, ['value', 'sliderValue']),
);
